import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import type { FC } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import {
  entityTypeToLinkStringMap,
  entityTypeToStringMap,
  SyncEntityDetailsV1,
} from '@/model/BenchlingBackfill.model.ts';
import Loading from '@/components/Loading';
import { SYNC_ENTITY_DISPLAY_COMPONENTS, SyncEntityPropRow } from './SyncEntityDisplayComponents.tsx';
import { web as webRoute } from '@/support/route';
import { _isNotEmpty } from '@/littledash.ts';
import { SyncEntityContext, SyncEntityDetailsProps } from './SyncEntityDetails.utils';

export const SyncEntityDetails: FC<SyncEntityDetailsProps> = ({ syncEntityRequestProps, entityType, closeModal }) => {
  const backfillSyncEvent = useApiHook({
    endpoint: 'GET /api/v1/backfill/{backfillApiId}/events/{backfillEventApiId}',
    path:
      syncEntityRequestProps.context === SyncEntityContext.BACKFILL_EVENTS
        ? {
            backfillApiId: syncEntityRequestProps.backfillApiId,
            backfillEventApiId: syncEntityRequestProps.backfillEventApiId,
          }
        : undefined,
    invokeOnInit: syncEntityRequestProps.context === SyncEntityContext.BACKFILL_EVENTS,
    options: { onError: { toast: false, capture: false, throw: true } },
  });

  const studySyncEvent = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/sync-events/{syncEventApiId}',
    path:
      syncEntityRequestProps.context === SyncEntityContext.STUDY_SYNC_EVENTS
        ? {
            studyApiId: syncEntityRequestProps.studyApiId,
            syncEventApiId: syncEntityRequestProps.syncEventApiId,
          }
        : undefined,
    invokeOnInit: syncEntityRequestProps.context === SyncEntityContext.STUDY_SYNC_EVENTS,
    options: { onError: { toast: false, capture: false, throw: true } },
  });

  const syncEvent = useApiHook({
    endpoint: 'GET /api/v1/sync-events/{syncEventApiId}',
    path:
      syncEntityRequestProps.context === SyncEntityContext.SYNC_EVENTS
        ? {
            syncEventApiId: syncEntityRequestProps.syncEventApiId,
          }
        : undefined,
    invokeOnInit: syncEntityRequestProps.context === SyncEntityContext.SYNC_EVENTS,
    options: { onError: { toast: false, capture: false, throw: true } },
  });

  // Get the active response and loading state based on context
  const getActiveHook = () => {
    switch (syncEntityRequestProps.context) {
      case SyncEntityContext.BACKFILL_EVENTS:
        return backfillSyncEvent;
      case SyncEntityContext.STUDY_SYNC_EVENTS:
        return studySyncEvent;
      case SyncEntityContext.SYNC_EVENTS:
        return syncEvent;
      default:
        throw new Error(`Unknown context`);
    }
  };

  const { response: syncEntityResponse, loading: syncEntityLoading } = getActiveHook();

  const syncEntity = syncEntityResponse?.body as SyncEntityDetailsV1;
  const entityDeletedAt =
    syncEntity?.entity_type !== 'studies'
      ? (syncEntity?.entity?.deleted_at ?? null)
      : (syncEntity?.study?.deleted_at ?? null);

  const studyDeleted = _isNotEmpty(syncEntity?.study?.deleted_at);

  const handleEntityNavigation = () => {
    if (!syncEntity) {
      return;
    }

    const studyId = syncEntity.study.id;

    let navigationUrl: string;

    switch (syncEntity.entity_type) {
      case 'studies':
        navigationUrl = webRoute('studies.show', {
          id: studyId,
        });
        break;

      case 'animals':
        navigationUrl = webRoute('animals.show.tab', {
          id: studyId,
          subject_id: syncEntity.entity.id,
          tab: 'overview',
        });
        break;

      case 'study_groups':
        navigationUrl = webRoute('studies.studyGroups', {
          id: studyId,
        });
        break;

      case 'treatments':
        navigationUrl = webRoute('studies.studyGroups.tab', { id: studyId, tab: 'treatments' });
        break;

      case 'dosages':
        navigationUrl = webRoute('animals.show.tab', {
          id: studyId,
          subject_id: syncEntity.entity.animal.id,
          tab: 'dosing',
        });
        break;

      case 'samples':
        navigationUrl = webRoute('animals.show.tab', {
          id: studyId,
          subject_id: syncEntity.entity.animal.id,
          tab: 'samples',
        });
        break;

      case 'measurements':
        navigationUrl = webRoute('animals.show.tab', {
          id: studyId,
          subject_id: syncEntity.entity.animal.id,
          tab: 'overview',
        });
        break;

      case 'observations':
        navigationUrl = webRoute('animals.show.tab', {
          id: studyId,
          subject_id: syncEntity.entity.animal.id,
          tab: 'observations',
        });
        break;

      default:
        return;
    }

    window.open(navigationUrl, '_blank');
  };

  if (syncEntityLoading) {
    return (
      <div style={{ height: 600 }}>
        <Loading txt="Loading" />
      </div>
    );
  }
  return (
    <ModalContainer size="narrow" testKey="sync-entity-details-modal">
      <ModalHeader
        title={entityTypeToStringMap?.[entityType] + ' details'}
        subText="Displayed details below reflect the item's current state."
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white "
      />

      <div className="pa3" data-test-component="SyncEntityDetails" data-test-element="container">
        {syncEntity &&
          (() => {
            const SyncEntityDisplayComponent = SYNC_ENTITY_DISPLAY_COMPONENTS[entityType];
            const entityData = entityType === 'studies' ? syncEntity.study : syncEntity.entity;

            return entityData ? (
              <SyncEntityDisplayComponent entity={entityData} />
            ) : (
              <div className="pa3 tc gray">
                <p>No entity data available</p>
              </div>
            );
          })()}
        {entityDeletedAt && (
          <div className="pt3 light-red">
            <SyncEntityPropRow label="Deleted at" value={entityDeletedAt} type="datetime" />
          </div>
        )}
      </div>

      <div className="pa3 bt b--moon-gray">
        <ModalActions
          onCancel={closeModal}
          cancelBtnText="Close"
          asideComponent={
            !entityDeletedAt && !studyDeleted ? (
              <div
                className="ml-auto pl3 flex items-center min-h-100"
                onClick={handleEntityNavigation}
                data-test-key={'ModalActions__execute-task'}
              >
                <p className="fw4 f5 link">{entityTypeToLinkStringMap?.[entityType]} </p>
              </div>
            ) : undefined
          }
        />
      </div>
    </ModalContainer>
  );
};
